import { Alert, AlertTitle, Box } from '@mui/material'
import React from 'react'

interface FormMessagesProps {
  validationErrors: string[]
  successMessage: string
  onClearMessage: () => void
  isRetrying?: boolean
}

/**
 * FormMessages - Component for displaying form validation errors and success messages
 *
 * Handles the display of error alerts and success notifications.
 */
export default function FormMessages({
  validationErrors,
  successMessage,
  onClearMessage,
  isRetrying = false
}: FormMessagesProps) {
  // Categorize errors for better user experience
  const getErrorSeverity = (error: string) => {
    if (
      error.includes('Network') ||
      error.includes('connection') ||
      error.includes('timeout')
    ) {
      return 'warning'
    }
    if (error.includes('permission') || error.includes('Authentication')) {
      return 'error'
    }
    return 'error' // Default for validation errors
  }

  const getErrorTitle = (error: string) => {
    if (error.includes('Network') || error.includes('connection')) {
      return 'Connection Issue'
    }
    if (error.includes('timeout')) {
      return 'Request Timeout'
    }
    if (error.includes('permission') || error.includes('Authentication')) {
      return 'Access Denied'
    }
    if (error.includes('quota')) {
      return 'Storage Limit'
    }
    if (error.includes('unavailable')) {
      return 'Service Unavailable'
    }
    return 'Validation Error'
  }

  return (
    <Box>
      {/* Validation Errors */}
      {validationErrors.length > 0 && !isRetrying && (
        <Alert
          severity={getErrorSeverity(validationErrors[0])}
          sx={{ mb: 2, borderRadius: 2 }}
          data-testid='validation-error-alert'
        >
          {validationErrors.length === 1 &&
            getErrorTitle(validationErrors[0]) !== 'Validation Error' && (
              <AlertTitle>{getErrorTitle(validationErrors[0])}</AlertTitle>
            )}
          {validationErrors.map((error, index) => (
            <div key={index} data-testid={`validation-error-${index}`}>
              {error}
            </div>
          ))}
        </Alert>
      )}

      {/* Retry Information */}
      {isRetrying && (
        <Alert severity='info' sx={{ mb: 2, borderRadius: 2 }}>
          <AlertTitle>Retrying Connection</AlertTitle>
          We're experiencing connection issues and will retry automatically.
          Please wait...
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert
          severity='success'
          onClose={onClearMessage}
          sx={{ mb: 2, borderRadius: 2 }}
          data-testid='useralert'
        >
          {successMessage}
        </Alert>
      )}
    </Box>
  )
}
